import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from "@material-ui/core";
import html2pdf from "html2pdf.js";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const addText = (parent, tag, text, style = {}) => {
  const element = document.createElement(tag);
  element.textContent = text || "";
  Object.assign(element.style, style);
  parent.appendChild(element);
  return element;
};

const buildDocument = (ticket, messages, ticketId) => {
  const root = document.createElement("div");
  Object.assign(root.style, { width: "760px", padding: "30px", color: "#162033", fontFamily: "Arial, sans-serif", background: "#fff" });
  addText(root, "h1", "Relatório de atendimento", { margin: "0 0 8px", fontSize: "25px" });
  addText(root, "div", `Ticket #${ticketId} • ${ticket?.contact?.name || "Contato"} • ${ticket?.contact?.number || ""}`, { color: "#526078", marginBottom: "6px" });
  addText(root, "div", `Gerado em ${new Date().toLocaleString("pt-BR")} • ${messages.length} mensagens`, { color: "#7a879c", fontSize: "12px", paddingBottom: "18px", borderBottom: "2px solid #e5eaf1" });
  messages.forEach(message => {
    const row = document.createElement("div");
    Object.assign(row.style, { display: "flex", justifyContent: message.fromMe ? "flex-end" : "flex-start", margin: "10px 0", pageBreakInside: "avoid" });
    const bubble = document.createElement("div");
    Object.assign(bubble.style, { maxWidth: "74%", padding: "10px 13px", borderRadius: "12px", background: message.fromMe ? "#dff6ec" : "#f0f3f7", border: "1px solid #dce3ec" });
    addText(bubble, "div", message.fromMe ? "Atendente" : (message.contact?.name || ticket?.contact?.name || "Cliente"), { fontSize: "11px", fontWeight: "bold", color: message.fromMe ? "#08775b" : "#315477", marginBottom: "4px" });
    if (message.body) addText(bubble, "div", message.body, { whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: "13px", lineHeight: "1.45" });
    if (message.mediaUrl) addText(bubble, "div", `[${message.mediaType || "arquivo"}] ${message.mediaUrl}`, { marginTop: "6px", color: "#2563a8", fontSize: "11px", overflowWrap: "anywhere" });
    addText(bubble, "div", new Date(message.createdAt).toLocaleString("pt-BR"), { textAlign: "right", color: "#78869a", fontSize: "9px", marginTop: "5px" });
    row.appendChild(bubble); root.appendChild(row);
  });
  addText(root, "div", "Fim do atendimento", { marginTop: "20px", paddingTop: "12px", borderTop: "1px solid #e5eaf1", textAlign: "center", color: "#8792a4", fontSize: "10px" });
  return root;
};

export default function TicketMessagesDialog({ open, handleClose, ticketId }) {
  const [status, setStatus] = useState("Preparando conversa...");
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState(false);
  const running = useRef(false);
  useEffect(() => {
    if (!open || running.current) return;
    running.current = true;
    let cancelled = false;
    const exportPDF = async () => {
      try {
        let page = 1, hasMore = true, ticket = null;
        const all = [];
        while (hasMore && !cancelled) {
          const { data } = await api.get(`/messages/${ticketId}`, { params: { pageNumber: page } });
          ticket = ticket || data.ticket;
          all.push(...data.messages);
          hasMore = data.hasMore;
          setProgress(data.count ? Math.min(90, Math.round(all.length / data.count * 90)) : 40);
          setStatus(`Carregando conversa: ${all.length} de ${data.count || all.length} mensagens`);
          page += 1;
        }
        if (cancelled) return;
        all.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setStatus("Montando páginas e gerando PDF..."); setProgress(95);
        await html2pdf().from(buildDocument(ticket, all, ticketId)).set({
          margin: [10, 8, 10, 8], filename: `atendimento_ticket_${ticketId}.pdf`,
          image: { type: "jpeg", quality: 0.96 }, html2canvas: { scale: 1.5, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }, pagebreak: { mode: ["css", "legacy"] }
        }).save();
        setProgress(100); setStatus("PDF gerado com sucesso.");
        setTimeout(() => { if (!cancelled) handleClose(); }, 700);
      } catch (err) { setFailed(true); setStatus("Não foi possível gerar o PDF."); toastError(err); }
      finally { running.current = false; }
    };
    exportPDF();
    return () => { cancelled = true; running.current = false; };
  }, [open, ticketId, handleClose]);
  return <Dialog maxWidth="sm" fullWidth open={open} onClose={failed ? handleClose : undefined}>
    <DialogTitle>Exportar conversa para PDF</DialogTitle>
    <DialogContent dividers><Box display="flex" alignItems="center" mb={2}>{!failed && progress < 100 && <CircularProgress size={25} style={{ marginRight: 12 }} />}<Typography>{status}</Typography></Box><LinearProgress variant="determinate" value={progress} color={failed ? "secondary" : "primary"} /></DialogContent>
    <DialogActions>{failed && <Button onClick={handleClose}>Fechar</Button>}</DialogActions>
  </Dialog>;
}
